import React, { useEffect, useState, useMemo } from 'react';
import { CheckCircle2, XCircle, Banknote, Filter } from 'lucide-react';
import DashboardShell from '@/components/layout/DashboardShell';
import api from '@/lib/api';
import { useAuth, formatApiError } from '@/lib/auth';
import { MODULES } from '@/lib/modules';

export default function PaymentNotices() {
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filterModule, setFilterModule] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const [n, a] = await Promise.all([
        api.get('/payments/notices'),
        api.get('/payments/accounts'),
      ]);
      setNotices(n.data.items || []); setAccounts(a.data.items || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const confirm = async (id) => {
    try { await api.post(`/payments/notices/${id}/confirm`); load(); }
    catch (e) { alert(formatApiError(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection?'); if (reason === null) return;
    try { await api.post(`/payments/notices/${id}/reject`, { reason }); load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    return notices.filter((n) => {
      if (filterStatus && n.status !== filterStatus) return false;
      if (filterModule && n.ref_kind !== filterModule) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(n.sender_name || '').toLowerCase().includes(s) && !(n.bank_name || '').toLowerCase().includes(s) && !(n.txn_ref || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [notices, filterStatus, filterModule, search]);

  const isManager = ['super_admin', 'org_manager'].includes(user?.role);

  return (
    <DashboardShell sectionTitle="Payments">
      <div className="mb-6">
        <h1 className="font-display font-bold text-3xl tracking-tight text-navy">Payments</h1>
        <p className="text-neutral-600 mt-1 text-sm">Bank-transfer payment notices, with filtering and confirmation flow.</p>
      </div>

      {/* Notices first (per user direction) */}
      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-display font-semibold text-lg text-navy">Payment notices</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Customers report transfers; managers confirm or reject.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sender, bank, txn ref…" className="px-3 py-1.5 text-sm border border-neutral-200 rounded-md outline-none w-64" data-testid="filter-search" />
              <select value={filterModule} onChange={(e) => setFilterModule(e.target.value)} className="px-3 py-1.5 text-sm border border-neutral-200 rounded-md outline-none bg-white" data-testid="filter-kind">
                <option value="">All kinds</option>
                <option value="invoice">Invoice</option>
                <option value="fee_bill">Fee bill</option>
                <option value="levy">Levy</option>
                <option value="service_request">Service request</option>
                <option value="donation">Donation</option>
                <option value="order">Order</option>
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-1.5 text-sm border border-neutral-200 rounded-md outline-none bg-white" data-testid="filter-status">
                <option value="">All status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50/50">
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-5 py-3">Reference</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Sender</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Amount</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Bank</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Date</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Status</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((n) => (
              <tr key={n.id} className="border-b border-neutral-100">
                <td className="px-5 py-3.5 font-mono text-xs">{n.ref_kind}/{(n.ref_id || '').slice(0, 8)}</td>
                <td className="px-3 py-3.5 text-sm text-navy">{n.sender_name || n.submitted_by_name || '—'}</td>
                <td className="px-3 py-3.5 font-mono text-sm">₦{Number(n.amount || 0).toLocaleString()}</td>
                <td className="px-3 py-3.5 text-sm text-neutral-700">{n.bank_name || '—'}</td>
                <td className="px-3 py-3.5 text-xs text-neutral-500 font-mono">{n.transfer_date || (n.created_at ? new Date(n.created_at).toLocaleDateString() : '—')}</td>
                <td className="px-3 py-3.5">
                  <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${n.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' : n.status === 'rejected' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>{n.status}</span>
                </td>
                <td className="px-3 py-3.5">
                  {n.status === 'pending' && isManager && (
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => confirm(n.id)} className="px-2 py-1 text-[11px] font-medium rounded hover:bg-emerald-50 text-emerald-700 inline-flex items-center gap-1" data-testid={`confirm-${n.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Confirm
                      </button>
                      <button onClick={() => reject(n.id)} className="px-2 py-1 text-[11px] font-medium rounded hover:bg-rose-50 text-rose-700 inline-flex items-center gap-1" data-testid={`reject-${n.id}`}>
                        <XCircle className="w-3 h-3" /> Reject
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-neutral-500 text-sm">No notices match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Bank accounts at bottom (per user direction) */}
      <div className="bg-white border border-neutral-200 rounded-md p-6">
        <div className="flex items-center gap-2 mb-3">
          <Banknote className="w-5 h-5 text-amber" />
          <h3 className="font-display font-semibold text-lg text-navy">Bank account details</h3>
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Transfer only</span>
        </div>
        <p className="text-sm text-neutral-600 mb-4">Customers transfer to these accounts. Payment gateways are scaffolded and ready to flip on when keys are provided.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map((a) => (
            <div key={a.id} className="p-5 border border-neutral-200 rounded-md hover:border-amber/40 transition-colors">
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">{a.module ? `${a.module} module` : 'All modules'}</div>
              <div className="font-display font-semibold text-navy">{a.bank_name}</div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">Account name</div>
                  <div className="font-medium text-navy">{a.account_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">Account number</div>
                  <div className="font-mono font-medium text-navy">{a.account_number}</div>
                </div>
              </div>
              {a.notes && <div className="mt-3 text-xs text-neutral-500 italic">{a.notes}</div>}
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}
